import { memo, useEffect, useMemo, useRef, useState } from 'react';
import type { FinalSong, SongMetadata } from '../../interfaces/final-song';
import type { ReportIssue } from '../../interfaces/report-issue';
import { ModalType } from '../../interfaces/modal-types.enum';
import { useGlobalStore } from '../../stores/global-store';
import { useUserStore } from '../../stores/user-store';
import { useDeviceStore } from '../../stores/device-store';
import { usePlayerStore } from '../../stores/player-store';
import { useToastStore } from '../../stores/toast-store';
import { useModalStore } from '../../stores/modal-store';
import { sendEventToServer } from '../../api/http-req';
import { useSongMetadataFetcher } from '../../queries/songs';
import { logger } from '../../shared/logging/logger';
import { trim } from '../../shared/utils/format';
import { useInView } from '../../shared/hooks/useInView';
import AppImage from '../../shared/components/AppImage';
import Popover from '../../shared/components/Popover';
import Tooltip from '../../shared/components/Tooltip';
import SocialSharing from '../../shared/components/SocialSharing';
import type { SocialSharingHandle } from '../../shared/components/SocialSharing';
import YouTubeIframe from '../../shared/components/YouTubeIframe';
import type { YouTubeIframeHandle } from '../../shared/components/YouTubeIframe';
import Mp3Player from '../../shared/components/Mp3Player';
import type { Mp3PlayerHandle } from '../../shared/components/Mp3Player';
import './ResultItem.scss';

// Port of src/app/shared/components/result-item/ (mypart-result-item).
//
// Angular → React mapping:
// - @Inputs song/songPositionInList/isYouTubeSuggestion/shouldPrioritize/isFirst
//   → props (same names/defaults; Angular had no other inputs).
// - The Angular call sites decorated the <mypart-result-item> HOST with
//   class="pop-animate max-w-[400px]" + [attr.data-result-item] + isInView
//   (main-content grid) or class="pop-animate" + isInView (YouTube suggestions
//   modal). Since React has no host element, this component renders that host
//   wrapper itself: pop-animate + isInView always, max-w-[400px] and
//   data-result-item only for grid items (!isYouTubeSuggestion) — exactly the
//   two existing usages.
// - constructor effect (highlightedSong → highlight + reset broken-link icon)
//   → derived store selector + useEffect.
// - ngOnInit (default playback source) → state initializer;
//   ngAfterViewInit (width measurement, resize
//   listener, share link) → layout-ish effect + useMemo. The Angular resize
//   listener was never removed; here it is cleaned up on unmount.
// - @ViewChild socialSharing/youtubeChild/mp3Child → imperative handles.
// - Manual tippy menus (moreMenu / brokenLinkMenu, tpTrigger="manual",
//   tpClassName="login-popper", tpPlacement="top-end") → controlled Popover
//   (floating-ui). A single `openMenu` state ('more' | 'broken' | null) drives
//   which menu is open; Popover handles toggling (useClick on the anchor),
//   outside-press, Esc and ancestor-scroll dismissal — replacing the Angular
//   showTooltip/hideTooltip + manual hide-on-scroll + useOutsideClick logic.
// - Angular mutated song.songAddedToPlaylist directly; preserved — re-render
//   is driven by subscribing to the global store's playlistSongs map, which
//   addRemoveSongFromPlaylist always updates.
// - The unused #showAdd ng-templates (dead code) were not ported.

type PlaybackSource = 'mp3' | 'youtube';

function getDefaultSource(song: FinalSong, isWhiteLabel: boolean): PlaybackSource {
  if (isWhiteLabel) {
    return song.url ? 'mp3' : 'youtube';      // WL: MP3-first, YouTube fallback
  }
  return song.youtube_id ? 'youtube' : 'mp3'; // Non-WL: unchanged from today
}

function ResultItem(props: {
  song: FinalSong;
  songPositionInList: number;
  isYouTubeSuggestion?: boolean;
  shouldPrioritize?: boolean;
  isFirst?: boolean;
  preloadAudio?: boolean;
}) {
  const { song, songPositionInList, isYouTubeSuggestion = false, shouldPrioritize = false, isFirst = false, preloadAudio = false } = props;

  const isMobileDevice = useDeviceStore(s => s.isMobileDevice);
  const isUserWhiteLabel = useUserStore(s => s.isUserWhiteLabel);
  const width = useGlobalStore(s => s.resultItemWidth);
  const highlightedSong = useGlobalStore(s => s.highlightedSong?.id === song.id);
  // Angular mutated song.songAddedToPlaylist and relied on CD; subscribe to
  // THIS song's membership (a boolean) so the icon updates on toggle. Selecting
  // the per-song boolean instead of the whole playlistSongs map means only the
  // toggled card re-renders, not every card in the grid.
  useGlobalStore(s => s.playlistSongs.has(song.id));
  const fetchSongMetadata = useSongMetadataFetcher();

  const [activeSource, setActiveSource] = useState<PlaybackSource>(
    () => getDefaultSource(song, useUserStore.getState().isUserWhiteLabel));

  const { ref: rootRef } = useInView<HTMLDivElement>();
  const youtubeRef = useRef<YouTubeIframeHandle>(null);
  const mp3Ref = useRef<Mp3PlayerHandle>(null);
  const socialSharingRef = useRef<SocialSharingHandle>(null);

  const [openMenu, setOpenMenu] = useState<'more' | 'broken' | null>(null);
  const toggleTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const songMetadataRef = useRef<SongMetadata | undefined>(undefined);
  const noMetadataRef = useRef(false);

  const newUrl = useRef(location.origin + '/').current;

  const imagePriority = shouldPrioritize || isFirst;

  const shareLink = useMemo(() => {
    const artist = song.artist.replaceAll(' ', '-');
    const title = song.title.replaceAll(' ', '-');
    return `https://www.mypart.com/songhunt/similar/${artist}-${title}`;
  }, [song.artist, song.title]);

  // ngAfterViewInit: measure first item + window resize listener (with the
  // Angular 100ms "el element init" timeout).
  useEffect(() => {
    const checkWidthAndHeight = () => {
      if (isFirst && rootRef.current) {
        const newWidth = rootRef.current.offsetWidth;
        const newHeight = newWidth + newWidth / 2;

        useGlobalStore.getState().setGlobalField('resultItemWidth', newWidth);
        useGlobalStore.getState().setGlobalField('resultItemHeight', newHeight);
      }
    };

    checkWidthAndHeight();

    let timer = 0;
    const onResize = () => {
      // timeout is needed to initialize el element
      timer = window.setTimeout(checkWidthAndHeight, 100);
    };
    window.addEventListener('resize', onResize, true);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('resize', onResize, true);
    };
  }, [isFirst, rootRef]);

  // ngOnDestroy: clear the deferred source-toggle timer.
  useEffect(() => {
    return () => clearTimeout(toggleTimerRef.current);
  }, []);

  const addRemoveSongFromPlaylist = () => {
    const globalState = useGlobalStore.getState();
    if (!song.songAddedToPlaylist && globalState.totalPlaylistSongs === 100) {
      useToastStore.getState().warning('You can add a maximum of 100 songs to a playlist');
      return;
    }

    song.songAddedToPlaylist = !song.songAddedToPlaylist;
    if (globalState.addRemoveSongFromPlaylist(song)) {
      const user = useUserStore.getState().user;
      sendEventToServer('SH_PLAYLIST_ADD', {
        'email': user ? user.email : 'no user',
        'artworkId': song.id,
        'url': window.location.href,
        'songIndex': songPositionInList
      });
    }
  };

  const openSpotifyTrack = (spotifyId?: string): void => {
    if (spotifyId) {
      window.open(`https://open.spotify.com/track/${spotifyId}`, '_blank');
    }
  };

  const canToggleSource = (): boolean => {
    return !!(song.url && song.youtube_id);
  };

  const toggleSource = () => {
    setOpenMenu(null);
    const next: PlaybackSource = activeSource === 'mp3' ? 'youtube' : 'mp3';
    setActiveSource(next);
    // Defer until React has swapped the inline child, then reuse the child's
    // existing play path (drives inline preview + bottom player). The child is
    // recreated by the swap, so calling its play method here starts playback
    // fresh. The stored handle is cleared on unmount so a recycled/destroyed
    // row cannot fire playback after teardown.
    toggleTimerRef.current = setTimeout(() => {
      if (next === 'youtube') {
        youtubeRef.current?.playVideo();
      } else {
        mp3Ref.current?.playUrl();
      }
    });
  };

  const playSongClicked = () => {
    const resultsContainer = document.getElementById('results-scroll-container')!;
    // Get the position of the current song element
    const position = rootRef.current!.getBoundingClientRect().top + resultsContainer.scrollTop;
    usePlayerStore.getState().setSongAndPosition(song, position);
    useGlobalStore.getState().setGlobalField('highlightedSong', song);
    logger.info('playback', 'play clicked', {
      songId: song.id,
      youtube_id: song.youtube_id,
      title: song.title,
      artist: song.artist,
      source: activeSource,
    });
  };

  const getUrlLinkForArtist = (artist: string) => {
    const newStr = artist.replaceAll(' ', '-');
    return `${newUrl}artist/${newStr}`;
  };

  const getUrlLinkForSong = (artist: string) => {
    const newStr = artist.replaceAll(' ', '-');
    return `${newUrl}similar/${newStr}`;
  };

  const showShareTooltip = () => {
    socialSharingRef.current?.showShareTooltip();
  };

  const openExtraInfoPopup = () => {
    setOpenMenu(null);
    if (songMetadataRef.current || noMetadataRef.current) {
      useModalStore.getState().open(ModalType.SONG_METADATA, {
        inputs: {
          noMetadata: noMetadataRef.current,
          songMetadata: songMetadataRef.current || {},
          artist: song.artist,
          title: song.title
        }
      });
      return;
    }

    fetchSongMetadata(song.id)
      .then((res) => {
        noMetadataRef.current = res === null;
        songMetadataRef.current = res ?? undefined;
        useModalStore.getState().open(ModalType.SONG_METADATA, {
          inputs: {
            noMetadata: noMetadataRef.current,
            songMetadata: res,
            artist: song.artist,
            title: song.title
          }
        });
      })
      .catch(() => {
        useToastStore.getState().warning('Could not get song info. please try again later');
      });
  };

  const reportIssue = () => {
    setOpenMenu(null);
    const reportIssueObj: ReportIssue = {
      artworkId: +song.id,
      youtubeId: song.youtube_id,
      comments: '',
      description: ''
    };
    useModalStore.getState().open(ModalType.REPORT_ISSUE, {
      inputs: {
        reportIssue: reportIssueObj
      }
    });
  };

  const playlistAddedImg = (
    <AppImage
        alt={'stylized red \'V\' on a black background. The \'V\' appears to have a sleek, modern design with sharp edges and a slightly metallic or glossy finish, giving it a three-dimensional appearance. The overall aesthetic is bold and striking, likely intended to capture attention with its vibrant color contrast against the dark backdrop.'}
        src="https://d382nvfdu38z2f.cloudfront.net/songhunt/red_v_optimized.png"
        fetchPriority="high" loading="eager"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
  );

  const playlistAddImg = (
    <AppImage
        alt={'The image shows a red plus with a white border. The design is simple and clean, with the red plus being centered and prominent'}
        src="https://d382nvfdu38z2f.cloudfront.net/songhunt/playlist_red_opt.png"
        fetchPriority="high" loading="eager"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
  );

  // Shared by the broken-link popover (non-white-label) and the white-label
  // more-menu's "Report Issue" item.
  const reportIssueIcon = (
    <svg className="w-6 h-6 text-mypart_red-500 ml-[6px] hover:drop-shadow-plus-light
       hover:dark:drop-shadow-plus-dark" fill="currentColor" height="24px"
         viewBox="0 0 511.999 511.999" width="24px" xmlns="http://www.w3.org/2000/svg">
      <g>
        <g>
          <path d="M500.232,11.766c-15.688-15.687-41.218-15.688-56.909,0L340.459,114.63c-41.716-21.472-94.286-14.79-129.196,20.122
                    L135.7,210.314c-34.91,34.91-41.594,87.481-20.122,129.196L11.767,443.323c-15.69,15.69-15.69,41.22,0,56.909
                    c7.846,7.844,18.15,11.766,28.454,11.766s20.61-3.922,28.454-11.766l103.787-103.787c15.514,8.013,32.824,12.284,50.814,12.284
                    c29.648,0,57.493-11.518,78.407-32.432c5.238-5.238,5.238-13.731,0-18.969l-37.939-37.939c-2.515-2.515-5.927-3.929-9.485-3.929
                    c-3.557,0-6.97,1.414-9.485,3.929c-1.607,1.606-3.375,2.986-5.25,4.176c4.79-14.069,1.591-30.282-9.609-41.482
                    c-11.2-11.199-27.413-14.398-41.482-9.608c1.188-1.875,2.57-3.644,4.176-5.25l75.562-75.562c1.388-1.388,2.883-2.602,4.451-3.663
                    c-5.006,14.169-1.855,30.603,9.459,41.918c7.846,7.844,18.15,11.766,28.454,11.766c4.563,0,9.125-0.773,13.474-2.311
                    c-1.073,1.58-2.294,3.074-3.673,4.453c-2.515,2.515-3.929,5.927-3.929,9.485c0,3.557,1.414,6.97,3.929,9.485l37.94,37.939
                    c5.238,5.238,13.731,5.237,18.969,0c34.91-34.91,41.594-87.481,20.122-129.196L500.231,68.674
                    C515.922,52.986,515.922,27.457,500.232,11.766z M253.147,346.729l19.333,19.333c-14.212,10.288-31.269,15.841-49.202,15.841
                    c-10.638,0-20.967-1.957-30.576-5.695l21.785-21.785c2.883,0.444,5.819,0.677,8.793,0.677
                    C233.992,355.101,244.251,352.185,253.147,346.729z M191.977,301.052c2.614-2.614,6.049-3.922,9.485-3.922
                    c3.435,0,6.87,1.308,9.485,3.922c5.23,5.23,5.23,13.739,0,18.969L49.706,481.262c-5.229,5.23-13.739,5.23-18.969,0
                    c-5.23-5.23-5.23-13.739,0-18.969L191.977,301.052z M249.202,172.692l-75.562,75.562c-10.796,10.796-16.743,25.168-16.743,40.468
                    c0,2.974,0.233,5.909,0.677,8.792l-21.757,21.757c-11.711-30.131-5.435-65.699,18.851-89.987l75.563-75.562
                    c16.387-16.387,37.912-24.58,59.438-24.58c10.378,0,20.752,1.913,30.552,5.726l-21.775,21.777
                    C281.024,153.957,262.594,159.3,249.202,172.692z M367.005,271.526l-19.329-19.327c5.455-8.896,8.371-19.154,8.371-29.869
                    c0-2.974-0.235-5.91-0.677-8.793l21.746-21.746C387.272,217.916,383.918,248.122,367.005,271.526z M481.263,49.705
                    L320.021,210.947c-5.229,5.23-13.739,5.23-18.969,0s-5.23-13.741,0-18.971L462.292,30.737c5.231-5.23,13.741-5.231,18.971,0
                    C486.493,35.967,486.493,44.476,481.263,49.705z"/>
        </g>
      </g>
      <g>
        <g>
          <path d="M420.56,400.641l-43.63-43.63c-5.238-5.238-13.731-5.238-18.969,0c-5.238,5.238-5.239,13.731,0,18.969l43.63,43.63
                  c2.62,2.62,6.052,3.929,9.485,3.929c3.432,0,6.866-1.31,9.485-3.929C425.798,414.373,425.799,405.879,420.56,400.641z"/>
        </g>
      </g>
      <g>
        <g>
          <path d="M476.759,327.83l-66.513-9.604c-7.33-1.057-14.135,4.025-15.193,11.359c-1.058,7.332,4.027,14.134,11.358,15.193
                    l66.513,9.604c0.651,0.094,1.296,0.14,1.934,0.14c6.559,0,12.295-4.817,13.259-11.498
                    C489.175,335.691,484.09,328.889,476.759,327.83z"/>
        </g>
      </g>
      <g>
        <g>
          <path d="M355.33,471.974l-9.604-66.512c-1.058-7.332-7.866-12.414-15.193-11.359c-7.332,1.06-12.417,7.86-11.359,15.193
                    l9.604,66.512c0.964,6.681,6.7,11.498,13.259,11.498c0.638,0,1.285-0.046,1.934-0.139
                    C351.304,486.108,356.389,479.307,355.33,471.974z"/>
        </g>
      </g>
    </svg>
  );

  const moreMenu = (
    <section className="more-menu-container">
      <ul>
        <li>
          <div onClick={openExtraInfoPopup} style={{ width: `${width - 32}px` }} className="li-container"
               role="button">
            <div className="left-child">
              <div className="flex items-center justify-center">
                <svg aria-hidden="true" className="w-6 h-6 text-mypart_red-500 ml-[6px] hover:drop-shadow-plus-light
                 hover:dark:drop-shadow-plus-dark" fill="none"
                     height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
                  <path
                      d="M4 18v-1a3 3 0 0 1 3-3h4a3 3 0 0 1 3 3v1a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1Zm8-10a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm8 10a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                      stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"
                      strokeWidth="2"/>
                </svg>
              </div>
            </div>

            <div className="right-child">
              <span>More Info</span>
            </div>
          </div>
        </li>
        {canToggleSource() && (
          <li>
            <div onClick={toggleSource} className="li-container" role="button">
              <div className="left-child">
                <div className="flex items-center justify-center">
                  <svg aria-hidden="true" className="w-6 h-6 text-mypart_red-500 ml-[6px] hover:drop-shadow-plus-light
                   hover:dark:drop-shadow-plus-dark" fill="none"
                       height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 7h13l-3-3m3 3-3 3M20 17H7l3-3m-3 3 3 3"
                          stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"
                          strokeWidth="2"/>
                  </svg>
                </div>
              </div>
              <div className="right-child">
                <span>{activeSource === 'mp3' ? 'Switch to YouTube' : 'Switch to MP3'}</span>
              </div>
            </div>
          </li>
        )}
        <li>
          <div onClick={showShareTooltip} className="li-container">
            <div className="left-child">
              <SocialSharing ref={socialSharingRef} link={shareLink} showTooltip={false}
                             youtubeId={song.youtube_id}
                             source="song" />
            </div>
            <div className="right-child">
              <span>Share This Song</span>
            </div>
          </div>
        </li>
        <li>
          <div onClick={addRemoveSongFromPlaylist} className="li-container">
            <div className="left-child">
              <div className="flex flex-col content-center p-1">
                <div className="block w-[22px] h-[22px] cursor-pointer relative hover:drop-shadow-plus-light
             hover:dark:drop-shadow-plus-dark">
                  {song.songAddedToPlaylist ? playlistAddedImg : playlistAddImg}
                </div>
              </div>
            </div>
            <div className="right-child">
              <span>{song.songAddedToPlaylist ? 'Remove from Playlist' : 'Add to Playlist'}</span>
            </div>
          </div>
        </li>
        {song.spotify_id && (
          <li>
            <div onClick={() => openSpotifyTrack(song.spotify_id)} className="li-container" role="link" tabIndex={0}>
              <div className="left-child">
                <div className="w-[22px] h-[22px] hover:drop-shadow-plus-light
                        hover:dark:drop-shadow-plus-dark">
                  <AppImage alt="Spotify logo" src="assets/spotify_logo.png"
                            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
                </div>
              </div>
              <div className="right-child">
                <span>Play on Spotify</span>
              </div>
            </div>
          </li>
        )}
        <li>
          <div onClick={reportIssue} className="li-container">
            <div className="left-child">
              {reportIssueIcon}
            </div>
            <div className="right-child">
              <span>Report Issue</span>
            </div>
          </div>
        </li>
      </ul>
    </section>
  );

  const brokenLinkMenu = (
    <section className="more-menu-container">
      <ul>
        <li>
          <div onClick={reportIssue} style={{ width: `${width - 32}px` }} className="li-container">
            <div className="left-child">
              {reportIssueIcon}
            </div>
            <div className="right-child">
              <span>Report Issue</span>
            </div>
          </div>
        </li>
      </ul>
    </section>
  );

  return (
    <div ref={rootRef}
         data-result-item={isYouTubeSuggestion ? undefined : song.youtube_id}
         data-preload-index={isYouTubeSuggestion ? undefined : songPositionInList}
         className={`result-item pop-animate${isYouTubeSuggestion ? '' : ' max-w-[400px]'}`}>
      <div
          className={`dark:bg-zinc-900 bg-gray-100 hover:shadow-red-950-md p-2 sm:p-0 transition-all${highlightedSong && !isYouTubeSuggestion ? ' outline outline-offset-2 outline-2 outline-zinc-900 dark:outline-zinc-100' : ''}`}
          role="status">
        <div style={{ height: `${width - 16}px` }}
             className="img-container">
          <AppImage alt={`Cover image for the song - ${song.title}, By - ${song.artist}`} checkPlaceholder
                    src={song.imageUrl}
                    fetchPriority={imagePriority ? 'high' : 'auto'}
                    loading={imagePriority ? 'eager' : 'lazy'}
                    className="shadow-xl object-cover rounded-t"
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
          {activeSource === 'youtube' ? (
            <YouTubeIframe
                ref={youtubeRef}
                onPlaySongClicked={playSongClicked}
                className={highlightedSong ? 'p-4' : undefined}
                timestamp={song.timestamp}
                youtube_id={song.youtube_id} />
          ) : (
            <Mp3Player
                ref={mp3Ref}
                onPlaySongClicked={playSongClicked}
                audioUrl={song.url}
                preloadAudio={preloadAudio}
                className={highlightedSong ? 'p-4' : undefined} />
          )}
          {!isYouTubeSuggestion && (
            <div className="absolute flex left-[2px] right-[2px] bottom-[2px]">
              {(song.lyricsMatch || song.isPopular) && (
                <>
                  {song.isPopular && (
                    <span className="rounded-md px-1 bg-green-500 text-white text-xs sm:text-sm mr-2">Popular</span>
                  )}
                  {song.lyricsMatch && (
                    <span className="rounded-md px-1 bg-mypart_red-500 text-white text-xs sm:text-sm">Lyric Match</span>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div className={`flex flex-col sm:flex-row items-center justify-between${isUserWhiteLabel ? ' !flex-row' : ''}`}>
          <div
              className={`flex text-container flex-col content-center justify-around px-1 w-full h-[72px] sm:w-[calc(100%-64px)]${isYouTubeSuggestion ? ' !px-2 !w-full' : ''}`}>
            <section title={`Discover songs similar to ${song.artist} - ${song.title}`}>
              <div className="song-title w-full inline-block dark:text-white text-base">
                <a href={getUrlLinkForSong(song.artist + ' ' + song.title)}
                   className="hover:underline"
                   target="_blank">
                  <p>{trim(song.title)}</p>
                </a>
              </div>
            </section>
            <section title={`Discover songs by ${song.artist}`}>
              <div className="artist-name w-full inline-block text-base text-gray-500 dark:text-gray-400 ">
                <a href={getUrlLinkForArtist(song.artist)}
                   className="hover:underline"
                   target="_blank">
                  {trim(song.artist)}
                </a>
              </div>
            </section>
          </div>
          {!isYouTubeSuggestion && (
            <section className="flex items-end mb-[-.5rem] sm:mb-0 w-full sm:w-auto justify-end sm:h-[60px]">
              {isUserWhiteLabel ? (
                <Popover open={openMenu === 'more'}
                         onOpenChange={(o) => setOpenMenu(o ? 'more' : null)}
                         popperClassName="login-popper" placement="top-end"
                         content={moreMenu}>
                  <svg
                       aria-label="More options" role="button"
                       className="cursor-pointer text-gray-800 dark:text-white order-1"
                       enableBackground="new 0 0 24 24" focusable="false"
                       height="36"
                       viewBox="0 0 24 24" width="36"
                       xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M12 16.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5-1.5-.67-1.5-1.5.67-1.5 1.5-1.5zM10.5 12c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5-.67-1.5-1.5-1.5-1.5.67-1.5 1.5zm0-6c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5-.67-1.5-1.5-1.5-1.5.67-1.5 1.5z"
                        fill="currentColor"></path>
                  </svg>
                </Popover>
              ) : (
                <section className="w-full h-full flex sm:flex-row justify-end sm:justify-start items-end mb-[4px]">
                  <SocialSharing link={shareLink} songInfo={`${song.artist} - ${song.title}`}
                                 youtubeId={song.youtube_id}
                                 className="sm:mr-[4px]" id="remove-padding"
                                 source="song" />
                  <Tooltip content={song.songAddedToPlaylist ? 'Remove from playlist' : 'Add to Playlist'}
                           disabled={isMobileDevice}>
                    <div onClick={addRemoveSongFromPlaylist}
                         className="block h-[22px] w-[22px] ml-[6px] sm:ml-0 cursor-pointer relative hover:drop-shadow-plus-light
                              hover:dark:drop-shadow-plus-dark">
                      {song.songAddedToPlaylist ? playlistAddedImg : playlistAddImg}
                    </div>
                  </Tooltip>
                  {song.spotify_id && (
                    <Tooltip content="Play on Spotify" disabled={isMobileDevice}>
                      <a href={'https://open.spotify.com/track/' + song.spotify_id}
                         className="block h-[22px] w-[22px] ml-[6px] cursor-pointer relative hover:drop-shadow-plus-light
                              hover:dark:drop-shadow-plus-dark sm:ml-[8px]"
                         target="_blank">
                        <AppImage alt="Spotify logo" src="assets/spotify_logo.png"
                                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
                      </a>
                    </Tooltip>
                  )}
                  <Popover open={openMenu === 'broken'}
                           onOpenChange={(o) => setOpenMenu(o ? 'broken' : null)}
                           popperClassName="login-popper" placement="top-end"
                           content={brokenLinkMenu}>
                    <svg
                         aria-label="Report an issue" role="button"
                         className="block h-[24px] w-[24px] ml-[6px] sm:ml-[8px] cursor-pointer text-gray-800 dark:text-white hover:drop-shadow-plus-light hover:dark:drop-shadow-plus-dark"
                         focusable="false"
                         viewBox="0 0 24 24"
                         xmlns="http://www.w3.org/2000/svg">
                      <title>Report an issue</title>
                      <path
                          d="M12 16.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5-1.5-.67-1.5-1.5.67-1.5 1.5-1.5zM10.5 12c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5-.67-1.5-1.5-1.5-1.5.67-1.5 1.5zm0-6c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5-.67-1.5-1.5-1.5-1.5.67-1.5 1.5z"
                          fill="currentColor"></path>
                    </svg>
                  </Popover>
                </section>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

// Memoized: the grid re-renders on unrelated global-store changes (e.g.
// showNoMoreResults toggling, infinite-scroll bookkeeping). With a stable `song`
// reference, memo lets each card skip those re-renders — meaningful since the
// grid renders many ResultItems. Reactive bits (playlistSongs, highlightedSong,
// etc.) are read from the store inside the component, so the card still updates
// when its own data changes.
export default memo(ResultItem);
